import request from '../utils/request';
import {
  CopyToUnitsPayload,
  CopyToUnitsResult,
  ProjectUnitsResponse,
  ReceivingCheckResult,
  ReceivingException,
  ReceivingExtraInput,
  ReceivingPlanItemInput,
  ReceivingRowInput,
  ReceivingRule,
  InspectionExecution,
  PieceAnswer,
  ReturnInspectionRule,
  ReturnInspectionRulePayload,
  ScopeLevel,
  RuleState,
  SourceFilter,
} from '../types/returnInspection';

const BASE = '/api/v1/return-inspection';

export interface ListRulesParams {
  /** 仅管理员有意义；客户账号的可见范围由后端按项目权限限定，传了也不会越权 */
  customer_id?: number;
  scope_level?: ScopeLevel;
  project_id?: number;
  inventory_unit_id?: number;
  state?: RuleState;
}

export async function listReturnInspectionRules(params?: ListRulesParams): Promise<ReturnInspectionRule[]> {
  const res = await request.get(`${BASE}/rules/`, { params });
  const data: any = res.data;
  return Array.isArray(data) ? data : data?.results || [];
}

export async function getReturnInspectionRule(id: number): Promise<ReturnInspectionRule> {
  const res = await request.get(`${BASE}/rules/${id}/`);
  return res.data;
}

/**
 * 保存规则。后端的语义是"发布新版本"：同一归属对象已有生效规则时，旧版本自动归档。
 * 客户级规则：主账号不需要传 customer_id（后端强制用自己）；管理员必须传目标客户。
 * 项目级/库存单元级规则：只传各自的目标 id，所属客户/项目由后端推导。
 */
export async function createReturnInspectionRule(payload: ReturnInspectionRulePayload): Promise<ReturnInspectionRule> {
  const res = await request.post(`${BASE}/rules/`, payload);
  return res.data;
}

export interface ListProjectUnitsParams {
  project_id: number;
  page?: number;
  page_size?: number;
  search?: string;
  /** 按规则来源筛选；none = 三级都没有规则 */
  source?: SourceFilter;
}

/**
 * 规则维护页的主数据接口：项目下的散货库存单元（分页）+ 每个单元的规则来源 + 整个项目的覆盖概览
 * + 通用规则/项目级规则。一次请求拿全。
 */
export async function listProjectUnits(params: ListProjectUnitsParams): Promise<ProjectUnitsResponse> {
  const res = await request.get(`${BASE}/rules/project-units/`, { params });
  return res.data;
}

/**
 * 移除本级的生效规则，之后这个归属对象自动回退到上级规则。
 * 不是物理删除，规则标记为"已移除"，历史完整保留。
 */
export async function removeReturnInspectionRule(id: number): Promise<ReturnInspectionRule> {
  const res = await request.post(`${BASE}/rules/${id}/remove/`, {});
  return res.data;
}

/**
 * 批量设置规则：把一个库存单元自设规则的"策略"（处理方式、检查流程、吸塑开关）复制给同一个项目下的其他库存单元。
 * 照片、文字说明、内件物品清单不会复制。
 */
export async function copyRuleToUnits(payload: CopyToUnitsPayload): Promise<CopyToUnitsResult> {
  const res = await request.post(`${BASE}/rules/copy-to-units/`, payload);
  return res.data;
}

/** 一个库存单元当前实际命中的规则（库存单元级 > 项目级 > 通用），source 为 null 表示三级都没有 */
export interface EffectiveUnitHit {
  inventory_unit_id: number;
  source: 'unit' | 'project' | 'customer' | null;
  rule_id: number | null;
  mode: ReturnInspectionRule['mode'] | null;
  version: number | null;
}

/** 创建退货预报时用：批量查一个项目下若干库存单元当前生效的规则（一次最多 500 个） */
export async function getEffectiveRules(projectId: number, inventoryUnitIds: number[]): Promise<{ units: EffectiveUnitHit[] }> {
  const res = await request.get(`${BASE}/rules/effective/`, {
    params: { project_id: projectId, inventory_unit_ids: inventoryUnitIds.join(',') },
  });
  return res.data;
}

export async function uploadBaselinePhoto(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await request.post<{ url: string }>(`${BASE}/upload-baseline-photo/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.url;
}

// ---------------------------------------------------------------------------
// 退货收货核对 / 收货异常
// ---------------------------------------------------------------------------

/** 核对一次收货（只读，不落库）：返回"一致/不一致"和逐行结果 */
export async function checkReceiving(payload: {
  forecast: number;
  rows: ReceivingRowInput[];
  extras: ReceivingExtraInput[];
}): Promise<ReceivingCheckResult> {
  const res = await request.post(`${BASE}/receiving/check/`, payload);
  return res.data;
}

/** 核对不一致：中断入库，创建收货异常（服务端会重新核对，一致则拒绝；至少 1 张留证照片） */
export async function createReceivingException(payload: {
  forecast: number;
  rows: ReceivingRowInput[];
  extras: ReceivingExtraInput[];
  photos: string[];
  remarks?: string;
}): Promise<ReceivingException> {
  const res = await request.post(`${BASE}/receiving/exceptions/`, payload);
  return res.data;
}

export async function listReceivingExceptions(params: { forecast?: number; status?: 'open' | 'resolved' }): Promise<ReceivingException[]> {
  const res = await request.get(`${BASE}/receiving/exceptions/`, { params });
  const data: any = res.data;
  return Array.isArray(data) ? data : data?.results || [];
}

/** 为异常选择处理方案：客户本人，或仓库在客户线下确认后代录入（必须写备注）；执行前可反复修改 */
export async function submitReceivingPlan(
  id: number,
  payload: { items: ReceivingPlanItemInput[]; acknowledged: boolean; note?: string }
): Promise<ReceivingException> {
  const res = await request.post(`${BASE}/receiving/exceptions/${id}/plan/`, payload);
  return res.data;
}

/** 撤销异常（仓库发现清点有误，重新清点）：必须写原因 */
export async function cancelReceivingException(id: number, note: string): Promise<ReceivingException> {
  const res = await request.post(`${BASE}/receiving/exceptions/${id}/cancel/`, { note });
  return res.data;
}

/** 执行退货入库初检（核对一致的自动方案，或异常里已确认的方案），一个事务内完成入库记录、库存动作和执行记录 */
export async function executeReturnInspection(payload: {
  forecast: number;
  /** 核对一致的路径需要（服务端重新核对）；按异常方案执行时不需要 */
  rows?: ReceivingRowInput[];
  extras?: ReceivingExtraInput[];
  answers: Record<string, PieceAnswer[]>;
  aiu_total_weight_g?: number | null;
  /** 有销毁的件时必填：销毁总重量（克） */
  destroy_total_weight_g?: number | null;
  photos?: string[];
  remarks?: string;
  tracking_number?: string;
}): Promise<InspectionExecution> {
  const res = await request.post(`${BASE}/receiving/execute/`, payload);
  return res.data;
}

export interface ReceivingRuleHit {
  inventory_unit_id: number;
  source: 'unit' | 'project' | 'customer';
  rule: ReceivingRule;
}

/** 仓库初检时对照用：这批库存单元当前生效的规则内容（流程、基准照片、清单） */
export async function getReceivingInspectionRules(forecast: number, inventoryUnitIds: number[]): Promise<ReceivingRuleHit[]> {
  const res = await request.get(`${BASE}/receiving/inspection-rules/`, {
    params: { forecast, inventory_unit_ids: inventoryUnitIds.join(',') },
  });
  return res.data.units;
}

export async function uploadReceivingPhoto(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await request.post<{ url: string }>(`${BASE}/receiving/upload-photo/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.url;
}
